'use client';

import { defineMessages, useIntl } from 'react-intl';

import { useFormatDiscount } from '@packages/shared/src/hooks/useFormatDiscount/useFormatDiscount';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';

import { Price } from '@packages/shared/src/ui/Price/Price';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { PriceValueLocalized } from '../Price/PriceValueLocalized/PriceValueLocalized';
import { PriceDiscountLocalized } from '../Price/PriceDiscountLocalized/PriceDiscountLocalized';
import { PriceUnitPriceLocalized } from '../Price/PriceUnitPriceLocalized/PriceUnitPriceLocalized';
import { priceUnitPriceMessages } from '../Price/PriceUnitPriceLocalized/messages';

export const priceMessages = defineMessages({
  msrp: {
    id: 'price.label.msrp',
    defaultMessage: 'UVP',
  },
  oldPrice: {
    id: 'price.label.oldPrice',
    defaultMessage: 'statt',
  },
  startPricePrefix: { id: 'price.start.from', defaultMessage: 'ab' },
});

export type PriceDiscount =
  | {
      previousPrice: number;
      wasMsrp?: boolean | null;
      discountPercentage?: number;
    }
  | {
      previousPrice: number;
      wasMsrp?: boolean | null;
      priceDifference?: number;
    };

export type ProductCardV2PriceProps = {
  id: string;
  price: {
    currentPrice: number;
    currency: string;
    isStartPrice?: boolean | null;
    discount?: PriceDiscount | null;
    unitPackage?: {
      price: number;
      amount: number;
      unit: string;
    } | null;
  };
  fixedValueTextClass?: string;
  className?: string;
};

export const ProductCardV2Price = ({
  className,
  id,
  price,
  fixedValueTextClass,
}: ProductCardV2PriceProps) => {
  const { formatPrice } = useFormatPrice();
  const { formatDiscount } = useFormatDiscount();
  const { formatMessage } = useIntl();

  return (
    <Price
      id={`product-${id}-price`}
      className={cn(
        'product-card-price mx-2 mt-1 grid-rows-subgrid [grid-area:4/2/span_5/span_1]',
        className,
      )}
    >
      {price.discount && (
        <PriceDiscountLocalized
          oldPrice={`${price.discount.wasMsrp ? formatMessage(priceMessages.msrp) : formatMessage(priceMessages.oldPrice)} ${formatPrice(price.discount.previousPrice, price.currency)}`}
          oldPriceType={price.discount.wasMsrp ? 'msrp' : undefined}
          discount={
            // eslint-disable-next-line no-nested-ternary
            'discountPercentage' in price.discount && price.discount.discountPercentage
              ? `- ${formatDiscount({ currency: price.currency, discountPercentage: price.discount.discountPercentage })}`
              : 'priceDifference' in price.discount && price.discount.priceDifference
                ? `- ${formatDiscount({ currency: price.currency, priceDifference: price.discount.priceDifference })}`
                : undefined
          }
        />
      )}
      <PriceValueLocalized fixedTextClass={fixedValueTextClass}>
        {price.isStartPrice ? `${formatMessage(priceMessages.startPricePrefix)} ` : ''}
        {formatPrice(price.currentPrice, price.currency)}
      </PriceValueLocalized>
      {price.unitPackage && (
        <PriceUnitPriceLocalized>
          {formatPrice(price.unitPackage?.price, price.currency)}
          <span className="sr-only">{formatMessage(priceUnitPriceMessages.perUnit)}</span>
          <span aria-hidden> / </span>
          {`${price.unitPackage.amount ? `${price.unitPackage.amount} ` : ''}${price.unitPackage.unit}`}
        </PriceUnitPriceLocalized>
      )}
    </Price>
  );
};
